import type { PostureStatus } from '@/components/ui/posture-chip';
import { cn } from '@/lib/utils/cn';
import { ChartTooltip, useChartTooltip } from './chart-tooltip';
import { ChartEmpty, POSTURE_FILL, POSTURE_LABEL, SrTable, cssVars, f1, plural } from './shared';
import { useEntryMotion } from './use-entry-motion';

/**
 * StackedColumns — domain ratings at each snapshot, then today (prototype
 * `postureColumns()`): one column per snapshot, stacked bottom to top
 * green, amber, red, then not rated, segments 2px apart, each domain one
 * unit of height. The counts per column come from the API (the frozen
 * snapshot, or today's posture counts).
 */
export interface PostureColumn {
  /** Axis label ("6 Oct", "Today"). */
  label: string;
  /** Today's live column: label in bold. */
  live?: boolean;
  counts: Record<PostureStatus, number>;
}

export interface StackedColumnsProps {
  columns: PostureColumn[];
  /** Domains on the engagement: the height of a full column. */
  total: number;
  label?: string;
  width?: number;
  height?: number;
  motionKey?: string | number;
  className?: string;
}

const M = { l: 26, r: 8, t: 10, b: 26 };
const BOTTOM_UP: PostureStatus[] = ['healthy', 'at-risk', 'critical', 'unassessed'];

export function StackedColumns({
  columns,
  total,
  label = 'Domains by rating at each snapshot',
  width: w = 480,
  height: h = 220,
  motionKey,
  className,
}: StackedColumnsProps) {
  const { animate, runKey } = useEntryMotion(motionKey);
  const { tip, bind } = useChartTooltip();

  if (!columns.length || total <= 0) {
    return (
      <ChartEmpty title="No snapshots yet" className={className}>
        Each snapshot freezes every domain&apos;s rating; the columns start with the first one.
      </ChartEmpty>
    );
  }

  const pw = w - M.l - M.r;
  const ph = h - M.t - M.b;
  const slot = pw / columns.length;
  const bw = Math.min(28, slot * 0.5);
  const unit = (ph - 2 * (total - 1)) / total;
  const ticks = [0];
  for (let v = 5; v <= total; v += 5) ticks.push(v);
  if (total < 5) ticks.push(total);

  return (
    <div key={runKey} className={cn(animate && 'chart-anim', className)}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={label}
        className="chart-svg block h-auto w-full overflow-visible"
      >
        {ticks.map((v) => {
          const y = M.t + ph - (v / total) * ph;
          return (
            <g key={`t${v}`}>
              <line x1={M.l} x2={M.l + pw} y1={f1(y)} y2={f1(y)} stroke="var(--ink-100)" />
              <text x={M.l - 6} y={f1(y + 4)} textAnchor="end">
                {v}
              </text>
            </g>
          );
        })}
        {columns.map((col, i) => {
          const x = M.l + slot * i + (slot - bw) / 2;
          let y = M.t + ph;
          const segs = BOTTOM_UP.filter((k) => col.counts[k] > 0).map((k) => {
            const n = col.counts[k];
            const sh = n * unit + (n - 1) * 2;
            y -= sh;
            const seg = (
              <rect
                key={k}
                x={f1(x)}
                y={f1(y)}
                width={f1(bw)}
                height={f1(sh)}
                rx={3}
                fill={POSTURE_FILL[k]}
                {...bind({
                  title: `${col.label} · ${POSTURE_LABEL[k]}`,
                  value: plural(n, 'domain'),
                })}
              />
            );
            y -= 2;
            return seg;
          });
          return (
            <g key={`c${i}`}>
              <g className="a-col" style={cssVars({ '--i': i })}>
                {segs}
              </g>
              <text
                x={f1(x + bw / 2)}
                y={h - 8}
                textAnchor="middle"
                className={col.live ? 'fill-ink-900 font-semibold' : undefined}
              >
                {col.label}
              </text>
            </g>
          );
        })}
      </svg>
      <SrTable
        caption={label}
        head={['Snapshot', 'Green', 'Amber', 'Red', 'Not rated']}
        rows={columns.map((c) => [
          c.label,
          c.counts.healthy,
          c.counts['at-risk'],
          c.counts.critical,
          c.counts.unassessed,
        ])}
      />
      <ChartTooltip tip={tip} />
    </div>
  );
}
